/**
 * B.12-R8.7 — «Создание доп.работы» bottom-sheet. Переделка 26.08 (владелец):
 * операторы и филиал — ЧИПАМИ (все видны сразу, мультиселект/один), период —
 * стандартный TimePicker mode="range" вместо нативных input type="time".
 * One row per operator is created; the day feed groups them into one card.
 * 11.09 (владелец): такси здесь НЕТ — его указывает исполнитель в отчёте о работе.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { CuratorPick } from "./CuratorPick";
import type { BoardGame, BoardOperator } from "../../hooks/useOoDay";
import type { AssignMap } from "./scoring";
import type { NewExtraWorkBase } from "../../hooks/useExtraWork";
import { fmtDateApi } from "../../utils/date";
import { TextPad, TimePicker } from "../../components/shared";
import { blurOtherField } from "../../components/shared/padFocus";
import { scrollCommentAboveDock } from "../../components/modal/padHost";
import { useChipSwipe } from "./useChipSwipe";
import { useClampPop } from "./useClampPop";
import css from "./board.module.css";
import { AvailBar, availSummary, busySummary } from "./AvailBar";

/* смахнуть чип — скрыть из списка (личная настройка ОО, как в графиках) */
const HIDE_LS = "ub_dop_hidden";

export function DopWorkSheet({
  open, onClose, date, operators, branches, games, assignMap, onCreateMany, mode = "dop",
}: {
  open: boolean;
  onClose: () => void;
  date: Date;
  operators: BoardOperator[];
  branches: string[];
  games: BoardGame[];
  assignMap: AssignMap;
  onCreateMany: (operatorIds: string[], base: NewExtraWorkBase) => Promise<{ ok: boolean; error?: string; warn?: string }>;
  /** 26.09 (владелец): «учёбка» — тот же шит, но ученики без оплаты и куратор на ставке. */
  mode?: "dop" | "study";
}) {
  const study = mode === "study";
  /* 26.09: скрытые чипы учёбки хранятся отдельно — спрятанный в допработах не пропадает из учеников */
  const hideKey = study ? "ub_study_hidden" : HIDE_LS;
  const [curatorId, setCuratorId] = useState("");
  const [opIds, setOpIds] = useState<Set<string>>(new Set());
  const [branchId, setBranchId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [timeOpen, setTimeOpen] = useState(false);
  const [desc, setDesc] = useState(""), [descPad, setDescPad] = useState(false);
  /* 22.09 (Саша: «пишу доп работу и не вижу что пишу»): наша клавиатура ложилась поверх
     поля. Высота дока поднимает шит — тот же приём, что в отчёте о доп.работе (12.09). */
  const [dockH, setDockH] = useState(0);
  const descRef = useRef<HTMLTextAreaElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleOp = (id: string) => {
    setOpIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    if (id === curatorId) setCuratorId("");
  };

  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { const v = JSON.parse(localStorage.getItem(hideKey) || "[]") as unknown; return new Set(Array.isArray(v) ? v.map(String) : []); } catch { return new Set(); }
  });
  const persistHidden = (n: Set<string>) => { try { localStorage.setItem(hideKey, JSON.stringify([...n])); } catch { /* private mode */ } };
  const hideOp = (id: string) => { setHidden((s) => { const n = new Set(s); n.add(id); persistHidden(n); return n; }); setOpIds((s) => { const n = new Set(s); n.delete(id); return n; }); };
  const unhide = (id: string) => setHidden((s) => { const n = new Set(s); n.delete(id); persistHidden(n); return n; });
  const showAllOps = () => { setHidden(new Set()); try { localStorage.removeItem(hideKey); } catch { /* private mode */ } };
  const { swDown, swMove, swUp, swMoved } = useChipSwipe(hideOp);
  /* 09.09 (владелец): удержание чипа — подробности графика человека на выбранный день */
  const [dayPop, setDayPop] = useState<string | null>(null);
  const lpT = useRef(0); const lpFired = useRef(false); const lpAt = useRef<{ x: number; y: number } | null>(null);
  const dayPopRef = useClampPop<HTMLDivElement>(dayPop);
  const lpDown = (id: string, e: React.PointerEvent) => {
    window.clearTimeout(lpT.current);   // прежний отсчёт мог остаться от быстрого повторного касания
    lpFired.current = false; lpAt.current = { x: e.clientX, y: e.clientY };
    lpT.current = window.setTimeout(() => { lpFired.current = true; setDayPop(id); }, 450);
    swDown(id, e);
  };
  const lpMove = (e: React.PointerEvent) => {
    const a = lpAt.current;
    if (a && (Math.abs(e.clientX - a.x) > 8 || Math.abs(e.clientY - a.y) > 8)) window.clearTimeout(lpT.current);
    swMove(e);
  };
  const lpUp = () => { window.clearTimeout(lpT.current); swUp(); };
  useEffect(() => () => window.clearTimeout(lpT.current), []);
  useEffect(() => { if (!open) { window.clearTimeout(lpT.current); setDayPop(null); } }, [open]);
  useEffect(() => { if (!dayPop) return;
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.availPop}`)) setDayPop(null); };
    document.addEventListener("pointerdown", onDown, true); return () => document.removeEventListener("pointerdown", onDown, true);
  }, [dayPop]);
  /** На какие игры дня человек уже поставлен — вторая половина ответа «свободен ли он». */
  const gamesOf = (opId: string) => games.filter((g) => (assignMap[g.id] ?? []).includes(opId))
    .map((g) => `${g.time} ${g.shortName}`);
  const [resetOpen, setResetOpen] = useState(false);
  const resetRef = useClampPop<HTMLDivElement>(resetOpen || null);
  useEffect(() => { if (!resetOpen) return; const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.addOpWrap}`)) setResetOpen(false); }; document.addEventListener("pointerdown", onDown, true); return () => document.removeEventListener("pointerdown", onDown, true); }, [resetOpen]);
  /* Шит поднялся — докручиваем поле описания к себе. Подъём идёт переходом 220мс, и пока он
     идёт, тело шита ещё высокое: scrollTop упирается в потолок и «не берётся». Ждём не по
     таймеру (его в фоне тормозит браузер), а по изменению самого блока — ResizeObserver
     срабатывает на каждом кадре анимации, последний раз — когда она закончилась. */
  useEffect(() => {
    if (!descPad) return;
    const el = descRef.current;
    const body = el?.closest<HTMLElement>(`.${css.asBody}`) ?? null;
    const run = () => { if (el) scrollCommentAboveDock(el, dockH); };
    run();
    if (!body || typeof ResizeObserver === "undefined") {
      const t = window.setTimeout(run, 320);
      return () => window.clearTimeout(t);
    }
    const ro = new ResizeObserver(run);
    ro.observe(body);
    return () => ro.disconnect();
  }, [descPad, dockH, desc]);

  const shownOps = operators.filter((o) => !hidden.has(o.id));

  const busyHours = useMemo(() => {
    if (opIds.size === 0) return [];
    return games
      .filter((g) => (assignMap[g.id] ?? []).some((o) => opIds.has(o)))
      .map((g) => `${g.time} ${g.shortName}`);
  }, [opIds, games, assignMap]);

  if (!open) return null;

  const valid = opIds.size > 0 && from && to && !!desc.trim();

  /** notify=false — «Сохранить»: записи есть, Telegram не уходит (26.09, как галочка на доске). */
  async function submit(notify = true) {
    if (!valid) { setErr(study ? "Выбери учеников, время и тему" : "Выбери операторов, время и описание"); return; }
    setBusy(true);
    const base = { date: fmtDateApi(date), time_from: from, time_to: to, description: desc.trim(), branch_id: branchId || undefined, ...(notify ? {} : { notify: false }) };
    /* Учёбка: ученики — kind 'training' (без оплаты), куратор — отдельной строкой
       'training_curator' (обычная допработа со ставкой). */
    let res = await onCreateMany([...opIds], study ? { ...base, kind: "training", curator_id: curatorId || undefined } : base);
    if (study && res.ok && curatorId) {
      const cur = await onCreateMany([curatorId], { ...base, kind: "training_curator" });
      res = cur.ok ? { ok: true, warn: res.warn ?? cur.warn } : { ok: false, error: `Ученики вызваны, но куратор не назначен: ${cur.error ?? "ошибка"}` };
    }
    setBusy(false);
    if (res.ok && res.warn) { setErr(res.warn); setOpIds(new Set()); setDesc(""); return; }
    if (res.ok) { onClose(); setOpIds(new Set()); setFrom(""); setTo(""); setDesc(""); setBranchId(""); setCuratorId(""); }
    else setErr(res.error ?? "Не удалось создать");
  }

  return (
    <>
      <div className={css.sheetBackdrop} onClick={onClose} />
      <div className={css.autoSheet} role="dialog" aria-modal="true"
        /* max(): на низком экране (альбом) 88dvh − клавиатура ушло бы почти в ноль — держим пол. */
        style={dockH ? { bottom: dockH, maxHeight: `max(34dvh, calc(88dvh - ${dockH}px))`, transition: "bottom 220ms var(--easing), max-height 220ms var(--easing)" } : undefined}>
        <div className={css.asHandle} />
        <div className={css.asHead}>
          <span className={css.asTitle}>{study ? "Учёбка" : "Создание доп.работы"}</span>
          <button type="button" className={css.asClose} onClick={onClose} aria-label="Закрыть">✕</button>
        </div>
        <div className={css.asBody}>
          <div className={css.asLbl}>{study ? "Ученики" : "Операторы"}{opIds.size ? ` · ${opIds.size}` : ""}{study ? <span className={css.asLblHint}> — без оплаты</span> : null}</div>
          <div className={css.dwChips}>
            {shownOps.map((o) => (
              <span key={o.id} className={css.chipWrap}>
                <button type="button" className={`${css.dwChip} ${opIds.has(o.id) ? css.on : ""}`}
                  title={`${o.name} · ${availSummary(o.avail)}; удержание — подробности дня, смахнуть — скрыть`}
                  onPointerDown={(e) => lpDown(o.id, e)} onPointerMove={lpMove}
                  onPointerUp={lpUp} onPointerCancel={lpUp} onContextMenu={(e) => e.preventDefault()}
                  onClick={() => { if (lpFired.current) { lpFired.current = false; return; } if (swMoved.current) { swMoved.current = false; return; } toggleOp(o.id); }}
                ><AvailBar avail={o.avail} />{o.name}</button>
                {dayPop === o.id ? (
                  <div className={css.availPop} ref={dayPopRef}>
                    <div className={css.availPopName}>{o.name}</div>
                    <div className={css.availPopLine}>
                      <span className={css.availPopKey}>свободен</span>
                      <span className={css.availPopFree}>{availSummary(o.avail)}</span>
                    </div>
                    {busySummary(o.avail) ? (
                      <div className={css.availPopLine}>
                        <span className={css.availPopKey}>занят</span>
                        <span className={css.availPopBusy}>{busySummary(o.avail)}</span>
                      </div>
                    ) : null}
                    <div className={css.availPopGames}>
                      {gamesOf(o.id).length ? `На играх: ${gamesOf(o.id).join(", ")}` : "На играх в этот день не стоит"}
                    </div>
                  </div>
                ) : null}
              </span>
            ))}
            {hidden.size ? (
              <span className={css.addOpWrap}>
                <button type="button" className={css.schedReset} title={`Скрытые: ${hidden.size}`} onClick={() => setResetOpen((v) => !v)} />
                {resetOpen ? (
                  <div className={css.mkOplist} ref={resetRef}>
                    {operators.filter((o) => hidden.has(o.id)).map((o) => (
                      <button key={o.id} type="button" className={css.opItem} onClick={() => unhide(o.id)}><span className={css.opName}>{o.name}</span></button>
                    ))}
                    <button type="button" className={css.opItem} onClick={() => { showAllOps(); setResetOpen(false); }}><span className={css.opName}><b>Восстановить всех</b></span></button>
                  </div>
                ) : null}
              </span>
            ) : null}
          </div>

          {opIds.size ? (
            <div className={css.dwBusy}>
              {busyHours.length ? <>Заняты: {busyHours.join(" · ")}</> : "Свободны весь день"}
            </div>
          ) : null}

          {/* 26.09: куратор — сразу за учениками, а не между полем темы и клавиатурой. */}
          {study ? <CuratorPick ops={shownOps} trainees={opIds} value={curatorId} onChange={setCuratorId} /> : null}

          <div className={css.asLbl}>Филиал</div>
          <div className={css.dwChips}>
            <button type="button" className={`${css.dwChip} ${branchId === "" ? css.on : ""}`} onClick={() => setBranchId("")}>любой</button>
            {branches.map((b) => (
              <button key={b} type="button" className={`${css.dwChip} ${branchId === b ? css.on : ""}`} onClick={() => setBranchId(b)}>{b}</button>
            ))}
          </div>

          <div className={css.asLbl}>Время</div>
          <button type="button" className={css.dwTimeBtn} onClick={() => setTimeOpen(true)}>
            {from && to ? `${from}–${to}` : "Выбрать период ›"}
          </button>

          <div className={css.asLbl}>{study ? "Тема" : "Описание"}</div>
          {/* 09.09 (владелец): здесь наша клавиатура, не системная — inputMode="none" её не поднимает,
              readOnly до открытия пада страхует WebView, которые этот признак не понимают. */}
          <textarea className={css.dwDesc} ref={descRef} value={desc} inputMode="none" readOnly={!descPad}
            data-pad-host="" placeholder="Напр. звук на ТАРО + сбор реквизита"
            onChange={(e) => setDesc(e.target.value)}
            onFocus={() => { blurOtherField(descRef.current); setDescPad(true); }}
            onPointerDown={() => setDescPad(true)} />

          {err ? <div className={css.actHint}>{err}</div> : null}
          {/* Пока клавиатура открыта — место под ней, чтобы поле можно было докрутить к себе. */}
          <div style={{ height: descPad ? 12 : 0 }} aria-hidden />
          <TextPad open={descPad} docked hostRef={descRef} multiline numRow label={study ? "Тема учёбки" : "Описание доп.работы"}
            value={desc} onChange={setDesc} onDockHeight={setDockH} onClose={() => { setDescPad(false); setDockH(0); }} />
          {timeOpen ? (
            <TimePicker
              open mode="range" step={5} label={study ? "Учёбка — время от и до" : "Доп — время от и до"}
              value={from && to ? `${from}–${to}` : undefined}
              onCommit={(v) => { const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/); if (m) { setFrom(m[1]!); setTo(m[2]!); } setTimeOpen(false); }}
              onCancel={() => setTimeOpen(false)}
            />
          ) : null}
        </div>
        {/* 19.10 (владелец: «доп работы рассылаются… после кнопки сохранить, без кнопки отправить»): «Сохранить» и у обычной
            допработы — карточка «не отправлено», уходит самолётиком или общей «Отправить»; главная кнопка — «…и отправить». */}
        <div className={`${css.asFoot} ${css.asFootPair}`}>
          <button type="button" className={css.asSave} onClick={() => void submit(false)} disabled={busy || !valid}
            title={study ? "Записать учёбку без сообщения в Telegram" : "Записать допработу без сообщения в Telegram — отправить потом"}>
            Сохранить
          </button>
          <button type="button" className={css.asRun} onClick={() => void submit()} disabled={busy || !valid}>
            {busy ? "Создаю…" : study ? "Вызвать на учёбку" : "Создать и отправить"}
          </button>
        </div>
      </div>
    </>
  );
}
